import React, { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Plus } from "lucide-react"

const services = [
  {
    id: 1,
    number: "01",
    title: "Architecture",
    description:
      "From first concept to construction handover — we provide end-to-end architectural services that deliver buildings with structural integrity, spatial intelligence, and lasting aesthetic value.",
    details: [
      "Concept Design & Feasibility",
      "Schematic & Design Development",
      "Construction Documentation",
      "Planning & Permit Applications",
      "Site Supervision & CA Services",
      "Post-occupancy Evaluation",
    ],
    image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&q=80&fit=crop",
    projects: 140,
    types: ["Residential", "Commercial", "Cultural", "Hospitality", "Mixed-Use"],
  },
  {
    id: 2,
    number: "02",
    title: "Interior Design",
    description:
      "We approach interiors as architecture at a human scale — curating material palettes, spatial sequences, and lighting strategies that create environments with genuine character and comfort.",
    details: [
      "Concept & Mood Development",
      "Space Planning & Layout",
      "Material & Finish Specification",
      "Custom Furniture Design",
      "Lighting Design",
      "FF&E Procurement & Styling",
    ],
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80&fit=crop",
    projects: 98,
    types: ["Residential", "Hospitality", "Corporate", "Retail", "Wellness"],
  },
  {
    id: 3,
    number: "03",
    title: "3D Visualization",
    description:
      "Our visualization studio produces photorealistic imagery and cinematic animations that communicate design intent with precision — from planning submissions to investor decks.",
    details: [
      "Architectural Stills & Renders",
      "Interior Visualizations",
      "Aerial & Flythrough Animations",
      "Interior Walkthrough Videos",
      "360° VR Experiences",
      "Real Estate Marketing Assets",
    ],
    image: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=800&q=80&fit=crop",
    projects: 220,
    types: ["Architecture", "Interior", "Urban", "Product", "Landscape"],
  },
  {
    id: 4,
    number: "04",
    title: "Urban Planning",
    description:
      "Masterplanning and urban design that navigates the complex interplay of community, infrastructure, and place-making — at every scale from the neighbourhood to the district.",
    details: [
      "Masterplanning & Urban Design",
      "Site Analysis & Feasibility",
      "Landscape Architecture",
      "Transport & Infrastructure",
      "Community Engagement",
      "Planning Strategy & Zoning",
    ],
    image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80&fit=crop",
    projects: 34,
    types: ["Mixed-Use", "Residential", "Civic", "Commercial", "Landscape"],
  },
]

export default function Services() {
  const [openId, setOpenId] = useState(1)

  return (
    <main className="pt-24 min-h-screen bg-stone-950">
      {/* Hero */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
          <span className="w-6 h-px bg-amber-400" />
          What We Offer
        </span>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
          <h1 className="font-['Cormorant_Garamond'] text-5xl lg:text-7xl font-light text-stone-100 leading-tight">
            Full-spectrum<br />
            <span className="italic text-stone-400">design services</span>
          </h1>
          <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed">
            Bastu Binyash offers an integrated suite of architectural and design services, bringing together spatial strategy, material craft, and visual storytelling under one roof.
          </p>
        </div>
      </div>

      {/* Services Accordion */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 pb-24">
        <div className="space-y-0 divide-y divide-stone-800">
          {services.map((service) => (
            <div key={service.id} className="group">
              {/* Accordion Header */}
              <button
                onClick={() => setOpenId(openId === service.id ? null : service.id)}
                className="w-full text-left py-8 flex items-center gap-6 group"
              >
                <span className="font-['Space_Grotesk'] text-xs text-stone-600 tracking-widest w-8">
                  {service.number}
                </span>
                <h2
                  className={`font-['Cormorant_Garamond'] text-3xl lg:text-5xl font-light transition-colors flex-1 ${
                    openId === service.id ? "text-amber-300" : "text-stone-100 group-hover:text-stone-200"
                  }`}
                >
                  {service.title}
                </h2>
                <span className="font-['Space_Grotesk'] text-xs text-stone-500 hidden lg:block">
                  {service.projects}+ projects
                </span>
                <div
                  className={`w-8 h-8 border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openId === service.id
                      ? "border-amber-400 bg-amber-400 rotate-45"
                      : "border-stone-700 rotate-0"
                  }`}
                >
                  <Plus
                    size={14}
                    className={openId === service.id ? "text-stone-950" : "text-stone-400"}
                  />
                </div>
              </button>

              {/* Accordion Content */}
              <div
                className={`overflow-hidden transition-all duration-500 ${
                  openId === service.id ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="pb-12 pl-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
                  {/* Description */}
                  <div className="lg:col-span-2 space-y-6">
                    <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
                      {service.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-center gap-3 font-['Inter'] text-sm text-stone-300"
                        >
                          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {service.types.map((type) => (
                        <span
                          key={type}
                          className="font-['Space_Grotesk'] text-[9px] tracking-[0.2em] text-stone-500 border border-stone-800 px-3 py-1.5 uppercase"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-3 font-['Space_Grotesk'] text-[11px] tracking-[0.2em] border border-amber-400 text-amber-400 px-6 py-3 hover:bg-amber-400 hover:text-stone-950 transition-all duration-300 group"
                    >
                      ENQUIRE ABOUT {service.title.toUpperCase()}
                      <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>

                  {/* Image */}
                  <div className="hidden lg:block overflow-hidden h-64">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process Section */}
      <div className="bg-stone-900 py-24 lg:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
          <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5">
            <span className="w-6 h-px bg-amber-400" />
            How We Work
          </span>
          <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 mb-16">
            Our Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", desc: "We listen deeply — understanding your vision, brief, site conditions, and constraints before any pencil touches paper." },
              { step: "02", title: "Concept", desc: "We develop multiple design directions, testing ideas through sketch, model, and precedent — then refine with you." },
              { step: "03", title: "Development", desc: "The chosen direction is developed into fully resolved technical drawings, material specifications, and 3D models." },
              { step: "04", title: "Delivery", desc: "From construction documentation to site supervision, we see every project through to a quality finish." },
            ].map((phase) => (
              <div key={phase.step} className="space-y-4">
                <div className="w-12 h-12 border border-stone-700 flex items-center justify-center">
                  <span className="font-['Space_Grotesk'] text-xs text-stone-500 tracking-widest">{phase.step}</span>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100">{phase.title}</h3>
                <p className="font-['Inter'] text-sm text-stone-500 leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  )
}
